// Zweck: Registrierungsstatus beim Einstieg in die Inbetriebnahme anzeigen.
// Kontext: Unbekannte/unerreichbare Zustände niemals als fehlende Freigabe werten. Stand: 2026-10-08
import { useEffect, useRef, useState } from 'react';
import { getMonitoringStatus } from '../services/api/monitoring';
import type { MonitoringStatus } from '../interfaces/monitoring';

interface MonitoringRow { text: string; warn: boolean }

function monitoringRows(status: MonitoringStatus): MonitoringRow[] {
  if (status.matchStatus === 'unavailable') return [{ text: 'Status konnte nicht geprüft werden.', warn: false }];
  if (status.matchStatus === 'unmatched' && !status.heatPumpSerial?.trim() && status.sourceUpdatedAt) {
    return [
      { text: 'WP in HubSpot fehlt: Keine Seriennummer hinterlegt.', warn: true },
      { text: 'Vaillant-Registrierung: Ohne Seriennummer nicht prüfbar.', warn: false },
      { text: 'Nutzung durch 360Volt: Ohne Seriennummer nicht prüfbar.', warn: false },
    ];
  }
  const texts = [
    status.heatPumpSerial?.trim() ? `WP in HubSpot hinterlegt: ${status.heatPumpSerial}` : 'WP in HubSpot: Status unbekannt.',
    status.matchStatus === 'unmatched' && status.heatPumpSerial?.trim()
      ? 'WP nicht bei Vaillant für 360Volt registriert.'
      : status.matchStatus === 'matched' && ['REGISTERED', 'CONSENT_PENDING', 'PENDING', 'CONSENT_REVOKED'].includes(status.registrationState)
        ? 'WP bei Vaillant erfasst.' : 'Vaillant-Registrierung: Status nicht eindeutig.',
    status.apiAccess === 'confirmed' ? 'Nutzung durch 360Volt freigegeben.'
      : status.apiAccess === 'pending' ? 'Nutzung durch 360Volt noch nicht freigegeben.'
        : status.apiAccess === 'revoked' ? 'Nutzung durch 360Volt: Freigabe widerrufen.'
          : 'Nutzung durch 360Volt: Freigabe nicht bestätigt.',
  ];
  return texts.map((text, index) => ({
    text,
    warn: (index === 1 && status.matchStatus === 'unmatched' && !!status.heatPumpSerial?.trim())
      || (index === 2 && (status.apiAccess === 'pending' || status.apiAccess === 'revoked')),
  }));
}

export default function InbetriebnahmeStatus({ dealId }: { dealId: string }) {
  const [rows, setRows] = useState<MonitoringRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const loadedDeal = useRef<string | null>(null);
  const saved = useRef<{ dealId: string; status: MonitoringStatus } | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  useEffect(() => {
    const initialLoad = loadedDeal.current !== dealId || attempt === 0;
    loadedDeal.current = dealId;
    const controller = new AbortController();
    setLoading(true);
    setRefreshFailed(false);
    const sameDeal = saved.current?.dealId === dealId;
    if (!sameDeal) {
      saved.current = null;
      setRows([]);
      setCheckedAt(null);
    }
    const display = (status: MonitoringStatus) => {
      setRows(monitoringRows(status));
      setCheckedAt(status.sourceUpdatedAt || null);
      if (status.sourceUpdatedAt && status.matchStatus !== 'unavailable') saved.current = { dealId, status };
    };
    const warn = (status: MonitoringStatus) => {
      const warnings = monitoringRows(status).filter(row => row.warn).map(row => row.text);
      if (initialLoad && warnings.length) window.alert(warnings.join('\n\n'));
    };
    const load = async () => {
      try {
        if (!navigator.onLine) throw new Error('offline');
        if (initialLoad) {
          const cached = await getMonitoringStatus(dealId, controller.signal, false, true);
          if (controller.signal.aborted) return;
          if (cached.sourceUpdatedAt && cached.matchStatus !== 'unavailable') {
            display(cached);
            const age = Date.now() - Date.parse(cached.sourceUpdatedAt);
            if (age >= 0 && age < 15 * 60 * 1000) {
              warn(cached);
              return;
            }
          }
        }
        const status = await getMonitoringStatus(dealId, controller.signal, !initialLoad);
        if (controller.signal.aborted) return;
        if (status.matchStatus === 'unavailable' && saved.current?.dealId === dealId) {
          setRefreshFailed(true);
          return;
        }
        display(status);
        warn(status);
      } catch (error) {
        if (controller.signal.aborted) return;
        if (saved.current?.dealId === dealId) {
          setRefreshFailed(true);
          return;
        }
        const forbidden = typeof error === 'object' && error !== null && 'status' in error && error.status === 403;
        setRows([{ warn: false, text: !navigator.onLine ? 'Offline: Status konnte nicht geprüft werden.' : forbidden
          ? 'Status konnte nicht geprüft werden: Der Zugriff ist für die Doku-App noch nicht freigegeben.'
          : 'Status konnte nicht geprüft werden. Bitte erneut versuchen.' }]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    void load();
    return () => controller.abort();
  }, [dealId, attempt]);
  return <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6" aria-label="Wärmepumpen-Status">
    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Wärmepumpen-Status</h2>
    <div role="status" className="text-sm text-gray-700 dark:text-gray-200 break-words">
      {loading && <p>Status wird geprüft …</p>}
      {rows.length > 0 && <ul className="list-disc pl-5 space-y-2">{rows.map(row => <li key={row.text}>{row.text}</li>)}</ul>}
      {checkedAt && <p className="mt-2">Gespeicherter Prüfstand: {new Date(checkedAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}. Keine Bestätigung des aktuellen Zugriffs.</p>}
      {refreshFailed && <p className="mt-2">Aktualisierung fehlgeschlagen. Angezeigt wird der gespeicherte Prüfstand.</p>}
    </div>
    <button type="button" disabled={loading} onClick={() => setAttempt(value => value + 1)} className="mt-3 min-h-[44px] text-primary-700 dark:text-primary-300 underline disabled:opacity-50">Erneut prüfen</button>
  </section>;
}
